// Projecting recurrence rules onto calendar dates.
//
// WeeklySchedule renders one week at a time, so most of its projection logic
// is week-shaped (isSessionActiveThisWeek). The bulk-period modal needs the
// same question answered across an arbitrary range — "which occurrences fall
// between these two dates?" — so the date-level primitives live here, shared
// by both.

import { ScheduleSession, RuleFrequency } from './SessionDetailPanel';
import { toDateString } from '../../services/dateUtils';

// getDay() (0=Sun..6=Sat) → weekday name, matching ScheduleSession.dayOfWeek.
export const WEEKDAY_BY_GETDAY = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

// Weekday name → days after that week's Monday (the app's 0=Monday convention).
export const DAY_OFFSETS: Record<string, number> = {
  Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3,
  Friday: 4, Saturday: 5, Sunday: 6,
};

/** All occursOnDate needs of a rule — so the stats, dashboard and charts,
 *  which hold rules in other shapes, can ask the same question. */
export type OccurrenceRule = Pick<ScheduleSession, 'dayOfWeek' | 'frequency' | 'startDate' | 'endDate'>;

/** The weekday names in the API's day_of_week order (0 = Monday). */
export const WEEKDAY_BY_INDEX = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

/**
 * A raw API rule (day_of_week index, repeat_interval) as an OccurrenceRule.
 * `null` for a 'daily' rule, which the weekly model doesn't draw and so never
 * occurs anywhere a grid, the dashboard or the stats look.
 */
export function ruleFromRow(row: {
  dayIdx: number;
  interval: string;
  isOnce: boolean;
  startDate: string;
  endDate: string | null;
}): OccurrenceRule | null {
  const frequency: RuleFrequency | null = row.isOnce ? 'once'
    : row.interval === 'weekly' ? 'weekly'
      : row.interval === 'bi-weekly' ? 'biweekly'
        : row.interval === 'monthly' ? 'monthly'
          : null;
  if (!frequency) return null;
  return {
    dayOfWeek: WEEKDAY_BY_INDEX[row.dayIdx],
    frequency,
    startDate: row.startDate,
    endDate: row.endDate,
  };
}

/** The weekday name of a YYYY-MM-DD date, in ScheduleSession.dayOfWeek form. */
export function weekdayOf(dateStr: string): string {
  return WEEKDAY_BY_GETDAY[new Date(dateStr + 'T00:00').getDay()];
}

/**
 * Whether a recurrence rule fires on a specific calendar date.
 *
 * A rule only ever fires on its own dayOfWeek, whatever its frequency. The
 * grid draws a session in its dayOfWeek column and nowhere else, so this is
 * what keeps the clash checks agreeing with what's on screen — without it a
 * one-off or monthly rule whose startDate sits on another weekday clashed on
 * a date where no bar is drawn.
 */
export function occursOnDate(s: OccurrenceRule, date: Date): boolean {
  const ds = toDateString(date);
  if (ds < s.startDate) return false;
  if (s.endDate && ds > s.endDate) return false;
  if (WEEKDAY_BY_GETDAY[date.getDay()] !== s.dayOfWeek) return false;
  const start = new Date(s.startDate + 'T00:00');
  switch (s.frequency) {
    case 'weekly':
      return true;
    case 'biweekly': {
      const diffDays = Math.round((date.getTime() - start.getTime()) / 86_400_000);
      return diffDays % 14 === 0;
    }
    case 'monthly':
      return date.getDate() === start.getDate();
    case 'once':
      return ds === s.startDate;
    default:
      return false;
  }
}

/** Every YYYY-MM-DD from `from` to `to` inclusive. Empty if the range is inverted. */
export function eachDateInRange(from: string, to: string): string[] {
  if (!from || !to || to < from) return [];
  const out: string[] = [];
  const cursor = new Date(from + 'T00:00');
  const end = new Date(to + 'T00:00');
  while (cursor.getTime() <= end.getTime()) {
    out.push(toDateString(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/** One projected session on one calendar date. */
export interface Occurrence {
  sessionId: string;        // local id, for touching WeeklySchedule state
  serverId: number;         // schedule_item.id — what the API writes against
  childId: number;
  date: string;             // YYYY-MM-DD
  startTime: string;
  endTime: string;
  dayOfWeek: string;
}

export interface OccurrenceFilters {
  /** Empty means every child the caller passed in. */
  childIds?: number[];
  /** Weekday names to keep. Empty/undefined means all. */
  weekdays?: string[];
}

/**
 * Project rules onto every date in [from, to].
 *
 * Rules with no serverId are skipped: they were never persisted, so there is
 * nothing to write an exception against. Callers are expected to have already
 * narrowed `sessions` to what the current user may see (the child whitelist).
 *
 * Sorted by date then start time so the preview reads chronologically.
 */
export function buildOccurrences(
  sessions: ScheduleSession[],
  from: string,
  to: string,
  filters: OccurrenceFilters = {},
): Occurrence[] {
  const { childIds, weekdays } = filters;
  const childFilter = childIds && childIds.length > 0 ? new Set(childIds) : null;
  const weekdayFilter = weekdays && weekdays.length > 0 ? new Set(weekdays) : null;

  const eligible = sessions.filter(s =>
    s.serverId !== undefined &&
    s.childId !== null &&
    (!childFilter || childFilter.has(s.childId)) &&
    (!weekdayFilter || weekdayFilter.has(s.dayOfWeek)),
  );
  if (eligible.length === 0) return [];

  const out: Occurrence[] = [];
  for (const dateStr of eachDateInRange(from, to)) {
    const date = new Date(dateStr + 'T00:00');
    for (const s of eligible) {
      if (!occursOnDate(s, date)) continue;
      out.push({
        sessionId: s.id,
        serverId: s.serverId!,
        childId: s.childId!,
        date: dateStr,
        startTime: s.startTime,
        endTime: s.endTime,
        dayOfWeek: s.dayOfWeek,
      });
    }
  }
  out.sort((a, b) => (a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date)));
  return out;
}
